import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Stack, Theme } from '@mui/material';
import Icon from '@/components/icon';
import Tooltip from '@/components/tooltip';
import BaseSwitch from '@/components/base-switch';
import Chip from '@/components/chip';
import classNames from 'classnames';

interface Props {
  value: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  xsmallIcon: {
    position: 'relative',
    height: 17,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleIcon: {
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
    width: 17,
    height: 17,
  },
  switch: {
    '& .MuiSwitch-track': {
      backgroundColor: `${theme.palette.toggle.offAlternate} !important`,
    },
    '& .Mui-checked + .MuiSwitch-track': {
      backgroundColor: `${theme.palette.primary.main} !important`,
    },
  },
}));

const MonitorSwitchV1: FC<Props> = ({ value, onChange, disabled }: Props) => {
  const styles = useStyles();

  const [checked, setChecked] = useState(value);

  const handleChange = () => {
    if (disabled) {
      return;
    }
    const updated = !checked;
    setChecked(updated);
    // A small delay to ensure transition effects are visible
    setTimeout(() => {
      onChange(updated);
    }, 200);
  };

  const tooltipTitle = (
    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
      {disabled && <Icon name="lock" size="x-small" className={styles.xsmallIcon} />}
      {checked ? 'Monitored' : 'Sensitive Data Protected'}
      {disabled && <Chip label="Default Policy" size="x-small" color="grey" />}
    </Stack>
  );

  const compClass = classNames(styles.xsmallIcon, styles.toggleIcon);

  return (
    <Tooltip title={tooltipTitle}>
      <BaseSwitch
        checked={!checked}
        onChange={handleChange}
        disabled={disabled}
        checkedIcon={<Icon name="security" size="x-small" color="grey" className={compClass} />}
        icon={<Icon name="monitor" size="x-small" color="grey" className={compClass} />}
        className={styles.switch}
      />
    </Tooltip>
  );
};

export default MonitorSwitchV1;
